import { Feature } from "@pentacode/core";
import type { MenuSelectEvent } from "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Checkbox } from "../../elements/checkbox";
import { DateInput } from "../../elements/date-input";
import "../../elements/help";
import "../../elements/popover";
import "../../elements/scroller";
import { IS_DEV } from "../../env";
import { app } from "../../init";
import { canAccess, menuData } from "../../lib/route-tree";
import { Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { type SettingsPage, settingsPages, settingsRoute } from "../app-routes";
import { routeAccess } from "../route-access";
import "./account";
import "./accounting";
import "./ai-assistant";
import "./benefits";
import "./billing";
import "./bonuses";
import "./company";
import "./core-data";
import "./documents";
import "./time";
import "./venues";

@customElement("ptc-settings")
export class Settings extends Routing(StateMixin(LitElement)) {
    routePattern = /^settings(?:\/(?<page>[\w-]+))?/;

    @state()
    private _loading: boolean = false;

    @routeProperty({ arg: "page" })
    private _page: SettingsPage;

    handleRoute() {
        if (!settingsPages.includes(this._page) || !this._canAccessPage(this._page)) {
            this.go(`settings/account`, undefined, true);
            return;
        }
    }

    private get _aiAssistantSettingsEnabled() {
        return IS_DEV || app.company?.features.includes(Feature.AiAssistantSettings);
    }

    private _canAccessPage(page: SettingsPage) {
        return canAccess(settingsRoute(page), routeAccess());
    }

    static styles = [
        shared,
        Checkbox.styles,
        DateInput.styles,
        css`
            :host {
                display: flex;
            }

            .sub-menu {
                width: 15em;
                overflow: auto;
            }

            .main {
                flex: 1;
                position: relative;
            }
        `,
    ];

    render() {
        if (!app.company) {
            return html``;
        }

        return html`
            <div class="sub-menu">
                <ptc-ui-menu
                    .groups=${menuData(settingsPages, settingsRoute, routeAccess())}
                    active=${this._page}
                    @menu-select=${(e: MenuSelectEvent) => this.go(`settings/${e.detail.id}`)}
                    ><slot name="submenu" slot="replace"></slot
                ></ptc-ui-menu>
            </div>

            <div class="main">
                <ptc-settings-account ?hidden=${this._page !== "account"}> </ptc-settings-account>

                <ptc-settings-company ?hidden=${this._page !== "company"}></ptc-settings-company>

                <ptc-settings-venues class="fullbleed" ?hidden=${this._page !== "venues"}></ptc-settings-venues>

                <ptc-settings-time class="fullbleed time" ?hidden=${this._page !== "time"}></ptc-settings-time>

                <ptc-settings-core-data
                    class="fullbleed"
                    ?hidden=${this._page !== "core-data"}
                ></ptc-settings-core-data>

                <ptc-settings-documents
                    class="fullbleed"
                    ?hidden=${this._page !== "documents"}
                ></ptc-settings-documents>

                <ptc-settings-bonuses class="fullbleed" ?hidden=${this._page !== "bonuses"}></ptc-settings-bonuses>

                <ptc-settings-benefits class="fullbleed" ?hidden=${this._page !== "benefits"}></ptc-settings-benefits>

                <ptc-settings-accounting
                    class="fullbleed"
                    ?hidden=${this._page !== "accounting"}
                ></ptc-settings-accounting>

                ${this._aiAssistantSettingsEnabled
                    ? html`<ptc-settings-ai-assistant
                          class="fullbleed"
                          ?hidden=${this._page !== "ai-assistant"}
                      ></ptc-settings-ai-assistant>`
                    : nothing}

                <ptc-settings-billing class="fullbleed" ?hidden=${this._page !== "billing"}></ptc-settings-billing>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
